import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, mergeMap, Observable, throwError } from 'rxjs';
import { StorageKeysEnum } from '../enums/storage.keys.enum';
import { AuthService } from './auth.service';
import { AuthUtils } from './auth.utils';

/**
 * Intercept
 *
 * @param req
 * @param next
 */
export const authInterceptor = (req: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> =>
{
    const authService = inject(AuthService);

    // Clone the request object
    let newReq = req.clone();

    // Request
    //
    // If the access token didn't expire, add the Authorization header.
    // We won't add the Authorization header if the access token expired.
    // This will force the server to return a "401 Unauthorized" response
    // for the protected API routes which our response interceptor will
    // catch and delete the access token from the local storage while logging
    // the user out from the app.
    if (authService.accessToken && !AuthUtils.isTokenValid(authService.accessToken, authService.accessTokenExpirationTime)) {
        newReq = req.clone({
            headers: req.headers.set('Authorization', 'Bearer ' + authService.accessToken)
        });
    }

    // Response
    return next(newReq).pipe(
        catchError((error) => {
            if (error.url.endsWith('refreshToken')) {
                // Sign out
                authService.logout(true);
                return throwError(() => error);
            }
            // Catch "401 Unauthorized" responses
            if (error instanceof HttpErrorResponse && error.status === 401) {
                if (error.url.endsWith('changeUserStatus')) {
                    return throwError(() => error);
                }
                if (authService.refreshTokenIsValid()) {
                    return authService.doRefreshToken().pipe(
                        catchError((err) => {
                            authService.logout(true);
                            return throwError(() => err);
                        }),
                        mergeMap(() => {
                            req = req.clone({
                                setHeaders: {
                                    Authorization: `Bearer ${localStorage.getItem(StorageKeysEnum.accessToken)}`
                                }
                            });
                            return next(req).pipe(
                                catchError((err) => {
                                    if (err instanceof HttpErrorResponse && err.status === 401) {
                                        authService.logout(true);
                                    }
                                    return throwError(() => err);
                                })
                            );
                        })
                    );
                } else {
                    // Sign out
                    authService.logout(true);
                    return throwError(() => error);
                }
            }

            return throwError(() => error);
        })
    );
};
